import { Link } from "react-router-dom";
import { ArrowRight, Play, CalendarDays, Receipt, Users, Sparkles, MessageSquare, CreditCard, Megaphone, Stethoscope } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import doctorPortrait from "@/assets/doctors/rezaul-karim.jpg";
import scheduleShot from "@/assets/hero/appointments.jpg";
import ehrShot from "@/assets/hero/analytics.jpg";

/**
 * Tebra-inspired "run your entire practice" showcase.
 * Three-panel composition: product screenshot card, doctor testimonial card,
 * dashboard card with floating module chips. Uses brand blue tokens.
 */
export default function PlatformShowcase() {
  const { lang } = useLang();
  const t = lang === "bn";

  const modules = [
    { icon: Stethoscope, label: t ? "ইএইচআর" : "EHR", href: "/features/prescriptions" },
    { icon: Users, label: t ? "রোগীর অভিজ্ঞতা" : "Patient Experience", href: "/features/patients" },
    { icon: Megaphone, label: t ? "প্র্যাকটিস মার্কেটিং" : "Practice Marketing", href: "/features/marketing" },
    { icon: CreditCard, label: t ? "বিলিং ও পেমেন্ট" : "Billing & Payments", href: "/features/billing" },
    { icon: Sparkles, label: t ? "এআই ও অটোমেশন" : "AI & Automation", href: "/features/ai-suite" },
  ];

  return (
    <section className="relative py-20 md:py-28 bg-gradient-to-b from-primary/5 via-background to-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Heading */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary mb-5">
            <Sparkles className="h-3.5 w-3.5" />
            {t ? "একটি প্ল্যাটফর্মে সবকিছু" : "One platform. Every workflow."}
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight text-foreground leading-[1.05]">
            {t ? (
              <>আপনার পুরো ক্লিনিক চালান <span className="text-primary">একটি EHR+ প্ল্যাটফর্মে</span></>
            ) : (
              <>Run your entire practice with <span className="text-primary">one EHR+ platform</span></>
            )}
          </h2>
          <p className="mt-5 text-lg text-muted-foreground">
            {t
              ? "DoctorsX-এর EHR+ কেয়ার, বিলিং, শিডিউলিং এবং রেপুটেশনকে যুক্ত করে। বিল্ট-ইন AI নোট দ্রুত করে, রিভিউ সামলায় এবং অ্যাডমিন কাজ অটোমেট করে।"
              : "DoctorsX's EHR+ connects care, billing, scheduling, and reputation. Built-in AI speeds up notes, handles reviews, and automates repetitive admin work."}
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/auth" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/90 transition">
              {t ? "ডেমো নিন" : "Get a demo"} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/features" className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground hover:bg-muted transition">
              {t ? "প্ল্যাটফর্ম ট্যুর" : "Take a quick tour"}
            </Link>
          </div>
        </div>

        {/* Three-card composition */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {/* Left: product screenshot in dark frame */}
          <div className="relative rounded-3xl bg-gradient-to-br from-primary via-primary to-primary/80 p-5 md:p-6 shadow-2xl shadow-primary/20 overflow-hidden group">
            <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-accent/40 blur-3xl" />
            <div className="relative rounded-2xl bg-background/95 backdrop-blur p-4 shadow-xl">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  <p className="text-xs font-semibold text-foreground">{t ? "অনলাইন শিডিউল" : "Online Scheduling"}</p>
                </div>
                <span className="text-[10px] text-muted-foreground">{t ? "আজ" : "Today"}</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative h-20 w-20 shrink-0">
                  <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="hsl(var(--muted))" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="hsl(var(--primary))" strokeWidth="3" strokeDasharray="97" strokeDashoffset="20" strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg font-bold text-foreground leading-none">2,340</span>
                    <span className="text-[9px] text-muted-foreground">{t ? "মোট" : "Total"}</span>
                  </div>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-primary" /><span className="text-foreground font-semibold">1,560</span><span className="text-muted-foreground">{t ? "নতুন" : "New"}</span></div>
                  <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-accent" /><span className="text-foreground font-semibold">780</span><span className="text-muted-foreground">{t ? "ফলোআপ" : "Return"}</span></div>
                </div>
              </div>
            </div>
            <div className="relative mt-4 rounded-2xl bg-background/95 backdrop-blur p-4 shadow-xl">
              <div className="flex items-center gap-2 mb-3">
                <Receipt className="h-4 w-4 text-primary" />
                <p className="text-xs font-semibold text-foreground">{t ? "স্বয়ংক্রিয় বিলিং" : "Automated Billing"}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t ? "সংগ্রহ" : "Collected"}</p>
                  <p className="text-base font-bold text-foreground">৳ 8,629</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t ? "পেমেন্ট" : "Payments"}</p>
                  <p className="text-base font-bold text-foreground">91</p>
                </div>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full w-3/4 rounded-full bg-gradient-to-r from-primary to-accent" />
              </div>
            </div>
          </div>

          {/* Middle: doctor testimonial with video overlay */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-foreground/10 min-h-[420px] md:min-h-full group">
            <img src={doctorPortrait} alt={t ? "ডাক্তার প্রশংসাপত্র" : "Doctor testimonial"} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/40 to-transparent" />
            <button aria-label={t ? "প্রশংসাপত্র চালান" : "Play testimonial"} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-16 w-16 rounded-full bg-background/90 backdrop-blur flex items-center justify-center shadow-2xl hover:scale-105 transition">
              <Play className="h-6 w-6 text-primary fill-primary ml-1" />
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-6 text-background">
              <p className="text-xs uppercase tracking-widest text-background/70 mb-2">{t ? "আমাদের গ্রাহকদের কাছ থেকে শুনুন" : "Hear from our customers"}</p>
              <h3 className="text-xl md:text-2xl font-display font-semibold leading-tight">
                {t ? "DoctorsX আমাদের গ্রোথের হাতিয়ার" : "DoctorsX gives us the tools to keep growing"}
              </h3>
              <p className="mt-3 text-sm text-background/80">
                {t ? "ডা. রেজাউল করিম — প্রতিষ্ঠাতা, প্রশান্তি ক্লিনিক" : "Dr. Rezaul Karim — Founder, Proshanti Clinic"}
              </p>
            </div>
          </div>

          {/* Right: dashboard with floating module chips */}
          <div className="relative rounded-3xl bg-gradient-to-br from-muted via-background to-primary/5 p-5 md:p-6 shadow-2xl shadow-primary/10 overflow-hidden">
            <div className="relative rounded-2xl overflow-hidden shadow-xl ring-1 ring-border">
              <img src={ehrShot} alt={t ? "ইএইচআর ড্যাশবোর্ড" : "EHR dashboard"} className="w-full h-56 md:h-64 object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent" />
            </div>
            {/* floating chips */}
            <div className="mt-5 flex flex-wrap gap-2">
              {modules.map(({ icon: Icon, label, href }) => (
                <Link
                  key={label}
                  to={href}
                  className="group inline-flex items-center gap-2 rounded-full bg-background border border-border pl-3 pr-2 py-1.5 text-xs font-semibold text-foreground shadow-md hover:shadow-lg hover:border-primary/40 hover:text-primary transition"
                >
                  <Icon className="h-3.5 w-3.5 text-primary" />
                  {label}
                  <span className="h-5 w-5 rounded-full bg-primary/10 group-hover:bg-primary group-hover:text-primary-foreground flex items-center justify-center transition">
                    <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              ))}
            </div>
            <div className="mt-4 rounded-2xl bg-background border border-border p-3 flex items-center gap-3 shadow-sm">
              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <MessageSquare className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground truncate">{t ? "AI নোট অ্যাসিস্ট চালু" : "AI Note Assist ready"}</p>
                <p className="text-[11px] text-muted-foreground truncate">{t ? "বাংলা SOAP নোট ~9 সেকেন্ডে" : "Bengali SOAP notes in ~9s"}</p>
              </div>
              <span className="ml-auto text-[10px] font-semibold text-primary bg-primary/10 rounded-full px-2 py-1">LIVE</span>
            </div>
          </div>
        </div>

        {/* Compliance strip */}
        <div className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-muted-foreground">
          <span>{t ? "আমরা আপনার প্র্যাকটিসকে সম্পূর্ণ নিরাপত্তা ও কমপ্লায়েন্স দিয়ে রক্ষা করি।" : "We safeguard your practice with full security and compliance."}</span>
          {["HIPAA", "HITRUST", "SOC 2", "PCI DSS", "DGHS"].map((b) => (
            <span key={b} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 font-semibold text-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />{b}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
